import { createSlice } from '@reduxjs/toolkit';
import { setPanelRole } from '../services/api';
import { resolveStoredAccess } from '../utils/accessSelection';

const readStoredClinic = (userUuid) => {
    try {
        const value = localStorage.getItem(`${userUuid}-clinic`);
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

const removeStoredAccess = (userUuid) => {
    if (!userUuid) return;
    localStorage.removeItem(userUuid);
    localStorage.removeItem(`${userUuid}-clinic`);
};

const persistAccess = (userUuid, roleUuid, clinic) => {
    if (!userUuid) return;
    localStorage.setItem(userUuid, roleUuid);
    if (clinic) {
        localStorage.setItem(`${userUuid}-clinic`, JSON.stringify(clinic));
    } else {
        localStorage.removeItem(`${userUuid}-clinic`);
    }
};

export const user = createSlice({
    name: 'user',
    initialState: {
        name: '',
        email: '',
        birthDate: '',
        gender: '',
        locale: '',
        sex: '',
        selectedRole: null,
        selectedClinic: null,
        roles: [],
        timezone: null,
        uuid: null,
        picture: null
    },
    reducers: {
        updateUser: (state, action) => {
            const roles = action.payload.roles || [];
            const storedRoleUuid = localStorage.getItem(action.payload.uuid);
            const storedClinic = readStoredClinic(action.payload.uuid);
            const storedAccess = resolveStoredAccess({
                roles,
                roleUuid: storedRoleUuid,
                clinic: storedClinic
            });

            state.name = action.payload.name;
            state.email = action.payload.email;
            state.birthDate = action.payload.birth_date;
            state.gender = action.payload.gender;
            state.locale = action.payload.locale;
            state.sex = action.payload.sex;
            state.selectedRole = storedAccess.isValid ? storedAccess.role.uuid : null;
            state.selectedClinic = storedAccess.clinic;
            state.roles = roles;
            state.timezone = action.payload.timezone;
            state.uuid = action.payload.uuid;
            state.picture = action.payload.picture;
            if (!storedAccess.isValid && !storedAccess.clinic) {
                removeStoredAccess(action.payload.uuid);
            } else if (!storedAccess.isValid) {
                localStorage.removeItem(action.payload.uuid);
            }
            setPanelRole(state.selectedRole);
        },
        updateRole: (state, action) => {
            const roleUuid = action.payload;
            const selection = resolveStoredAccess({
                roles: state.roles,
                roleUuid,
                clinic: state.selectedClinic
            });
            if (!selection.isValid) return;

            setPanelRole(roleUuid);
            persistAccess(state.uuid, roleUuid, selection.clinic);
            state.selectedRole = roleUuid;
            state.selectedClinic = selection.clinic;
        },
        updateClinic: (state, action) => {
            const clinic = action.payload;
            state.selectedClinic = clinic;
            state.selectedRole = null;
            localStorage.setItem(`${state.uuid}-clinic`, JSON.stringify(clinic));
            localStorage.removeItem(state.uuid);
            setPanelRole(null);
        },
        selectAccess: (state, action) => {
            const { clinic, roleUuid } = action.payload;
            const selection = resolveStoredAccess({
                roles: state.roles,
                roleUuid,
                clinic
            });
            if (!selection.isValid) return;

            state.selectedClinic = selection.clinic;
            state.selectedRole = selection.role.uuid;
            persistAccess(state.uuid, selection.role.uuid, selection.clinic);
            setPanelRole(selection.role.uuid);
        },
        clearAccessSelection: (state) => {
            removeStoredAccess(state.uuid);
            state.selectedRole = null;
            state.selectedClinic = null;
            setPanelRole(null);
        },
        resetUser: (state) => {
            state.name = '';
            state.email = '';
            state.birthDate = '';
            state.gender = '';
            state.locale = '';
            state.sex = '';
            state.selectedRole = null;
            state.selectedClinic = null;
            state.roles = [];
            state.timezone = null;
            state.uuid = null;
            state.picture = null;
            setPanelRole(null);
        }
    }
});

export const {
    updateUser,
    updateRole,
    updateClinic,
    selectAccess,
    clearAccessSelection,
    resetUser
} = user.actions;

export default user.reducer;
